import React from 'react';
import { useFormContext } from 'react-hook-form';
import { HelperText, TextInput, useTheme } from 'react-native-paper';
import styled from 'styled-components/native';

export type Props = React.ComponentProps<typeof TextInput> & {
  name: string;
  label?: React.ReactNode | string;
  compact?: boolean;
  helperMessage?: string;
  affix?: string;
};

const Container = styled.View`
  flex-grow: 1;
`;

const Component: React.FC<Props> = React.forwardRef((_props, ref) => {
  const {
    name,
    defaultValue,
    style,
    compact,
    helperMessage,
    affix,
    right,
    ...props
  } = _props;
  const { register, unregister, errors, setValue, watch } = useFormContext();
  const theme = useTheme();
  const error = errors?.[name];
  const value = watch(name, defaultValue);

  React.useEffect(() => {
    if (defaultValue) {
      setValue(name, defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  React.useEffect(() => {
    register(name);
    return () => unregister(name);
  }, [name, register, unregister]);

  const handleChangeText = React.useCallback(
    (input: any) => {
      setValue(name, input);
    },
    [name, setValue]
  );

  return (
    <Container>
      <TextInput
        style={[{ backgroundColor: theme.colors.background }, style]}
        selectionColor={theme.colors.primary}
        mode="outlined"
        defaultValue={defaultValue}
        onChangeText={handleChangeText}
        value={value ?? ''}
        right={right ? right : affix && <TextInput.Affix text={affix} />}
        error={!!error}
        ref={ref}
        {...props}
      />
      {!compact && !error && (
        <HelperText type="info" visible={!!helperMessage && !error}>
          {helperMessage}
        </HelperText>
      )}
      {!compact && (
        <HelperText type="error" visible={!!error}>
          {error?.message}
        </HelperText>
      )}
    </Container>
  );
});

Component.displayName = 'TextInput';

export default Component;
